/**
 * Pure parsing and classification for the Log ROs screen (WARP-942).
 *
 * Kept free of React and of any service import so it can be unit-tested on its own,
 * the same split `overdue.js` and `advisor.js` already use in this folder.
 */

/** `RepairOrder.ro_number` is `UppercaseCharField(max_length=255)` on the backend. */
export const RO_NUMBER_MAX_LENGTH = 255

export const ROW_STATUS = {
  NEW: 'new',
  DUPLICATE: 'duplicate',
  INVALID: 'invalid',
}

export const ROW_SOURCE = {
  PASTED: 'Pasted',
  MANUAL: 'Manual',
}

/**
 * The backend stores RO numbers uppercased (`UppercaseCharField.get_prep_value`), so
 * every comparison — within a batch and against what the dealer already has — happens
 * on the uppercased value.
 */
export const normalizeRoNumber = (value) =>
  String(value ?? '')
    .trim()
    .toUpperCase()

/**
 * Split a pasted blob into candidate RO numbers.
 *
 * Deliberately permissive. WARP puts no format rule on `ro_number` at any layer, and
 * real dealer numbers are not all six digits, so anything that filtered by shape would
 * silently drop valid ROs. Whatever survives is shown in the preview for the user to
 * confirm or remove, which is the point of the screen.
 */
export const tokenizeRoList = (text) =>
  String(text ?? '')
    .split(/[\s,;|]+/)
    .map((token) => normalizeRoNumber(token))
    .filter(Boolean)

/** Returns null when the number is usable, or a short reason when it is not. */
export const validateRoNumber = (roNumber) => {
  if (!roNumber) return 'Empty'
  if (roNumber.length > RO_NUMBER_MAX_LENGTH) {
    return `Longer than ${RO_NUMBER_MAX_LENGTH} characters`
  }
  return null
}

/**
 * Turn raw values into preview rows, collapsing repeats within the batch and keeping
 * the order they were entered in.
 */
export const buildRows = (values, source, makeId = () => crypto.randomUUID()) => {
  const seen = new Set()

  return values.reduce((rows, value) => {
    const roNumber = normalizeRoNumber(value)
    if (!roNumber || seen.has(roNumber)) return rows

    seen.add(roNumber)
    rows.push({ id: makeId(), roNumber, source, files: [], error: null })
    return rows
  }, [])
}

/**
 * Stamp each row with its preview status.
 *
 * `existingRoNumbers` is what the dealer already has, from the API. A number already
 * logged is a hard block: it is reported as skipped and never posted.
 */
export const classifyRows = (rows, existingRoNumbers = []) => {
  const existing = new Set(existingRoNumbers.map(normalizeRoNumber))

  return rows.map((row) => {
    const invalidReason = validateRoNumber(row.roNumber)
    if (invalidReason) {
      return { ...row, status: ROW_STATUS.INVALID, note: invalidReason }
    }
    if (existing.has(row.roNumber)) {
      return {
        ...row,
        status: ROW_STATUS.DUPLICATE,
        note: 'Skipped – already logged for this dealer',
      }
    }
    return { ...row, status: ROW_STATUS.NEW, note: '' }
  })
}

/** Counts behind the preview's summary line. */
export const summarizeRows = (rows) => ({
  total: rows.length,
  newCount: rows.filter((row) => row.status === ROW_STATUS.NEW).length,
  duplicateCount: rows.filter((row) => row.status === ROW_STATUS.DUPLICATE).length,
  invalidCount: rows.filter((row) => row.status === ROW_STATUS.INVALID).length,
})

/** The summary line under the preview — "23 new · 8 already logged". */
export const summaryLabel = (rows) => {
  const { newCount, duplicateCount, invalidCount } = summarizeRows(rows)
  if (rows.length === 0) return ''

  const parts = [`${newCount} new`]
  if (duplicateCount > 0) parts.push(`${duplicateCount} already logged`)
  if (invalidCount > 0) parts.push(`${invalidCount} unusable`)
  return parts.join(' · ')
}
